import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { appConfig, isOfflineMode } from '../config/appConfig';

export interface License {
  key: string;
  type: 'offline' | 'cloud';
  clientId: string;
  clientName: string;
  issuedAt: string;
  expiresAt: string | null; // null for lifetime offline licenses
  features: string[];
  status: 'active' | 'expired' | 'invalid';
}

export interface LicenseValidationResult {
  valid: boolean;
  license: License | null;
  error?: string;
}

class LicenseService {
  private readonly LICENSE_KEY = 'hmpos_license';
  private license: License | null = null;

  async init(): Promise<void> {
    await this.loadLicense();
  }

  private async loadLicense(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: this.LICENSE_KEY });
      if (value) {
        this.license = JSON.parse(value);
        // Check if expired
        if (this.license && this.license.expiresAt && new Date(this.license.expiresAt) < new Date()) {
          this.license.status = 'expired';
          await this.saveLicense();
        }
      }
    } catch (error) {
      console.error('Failed to load license:', error);
    }
  }

  private async saveLicense(): Promise<void> {
    if (this.license) {
      await Preferences.set({
        key: this.LICENSE_KEY,
        value: JSON.stringify(this.license),
      });
    }
  }

  async validateLicense(key: string): Promise<LicenseValidationResult> {
    try {
      // For offline mode, validate locally
      if (isOfflineMode()) {
        return this.validateOfflineLicense(key);
      }

      // For cloud mode, validate against server
      return this.validateCloudLicense(key);
    } catch (error) {
      return {
        valid: false,
        license: null,
        error: error instanceof Error ? error.message : 'Validation failed',
      };
    }
  }

  private async validateOfflineLicense(key: string): Promise<LicenseValidationResult> {
    // Simple offline license validation
    // In production, you'd use cryptographic validation
    try {
      // Decode the license key (base64 encoded JSON)
      const decoded = atob(key);
      const licenseData: License = JSON.parse(decoded);

      // Validate structure
      if (!licenseData.key || !licenseData.type || !licenseData.clientId) {
        return {
          valid: false,
          license: null,
          error: 'Invalid license format',
        };
      }

      // Validate type
      if (licenseData.type !== 'offline') {
        return {
          valid: false,
          license: null,
          error: 'Invalid license type for offline mode',
        };
      }

      // Check expiry
      if (licenseData.expiresAt && new Date(licenseData.expiresAt) < new Date()) {
        licenseData.status = 'expired';
        return {
          valid: false,
          license: licenseData,
          error: 'License has expired',
        };
      }

      licenseData.status = 'active';
      this.license = licenseData;
      await this.saveLicense();

      return {
        valid: true,
        license: licenseData,
      };
    } catch (error) {
      return {
        valid: false,
        license: null,
        error: 'Invalid license key format',
      };
    }
  }

  private async validateCloudLicense(key: string): Promise<LicenseValidationResult> {
    try {
      const response = await fetch(`${appConfig.serverUrl}/api/license/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key }),
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          valid: false,
          license: null,
          error: error.message || 'Server validation failed',
        };
      }

      const result = await response.json();
      const license: License = result.license;

      if (license.expiresAt && new Date(license.expiresAt) < new Date()) {
        license.status = 'expired';
        return {
          valid: false,
          license: license,
          error: 'License has expired',
        };
      }

      license.status = 'active';
      this.license = license;
      await this.saveLicense();

      return {
        valid: true,
        license: license,
      };
    } catch (error) {
      return {
        valid: false,
        license: null,
        error: 'Failed to validate license with server',
      };
    }
  }

  async getLicense(): Promise<License | null> {
    if (!this.license) {
      await this.loadLicense();
    }
    return this.license;
  }

  async isLicenseValid(): Promise<boolean> {
    const license = await this.getLicense();
    return license !== null && license.status === 'active';
  }

  async clearLicense(): Promise<void> {
    this.license = null;
    await Preferences.remove({ key: this.LICENSE_KEY });
  }

  async generateOfflineLicense(clientId: string, clientName: string): Promise<string> {
    const license: License = {
      key: this.generateKey(),
      type: 'offline',
      clientId,
      clientName,
      issuedAt: new Date().toISOString(),
      expiresAt: null, // Lifetime license
      features: ['local_backup', 'local_restore', 'export', 'import'],
      status: 'active',
    };

    // Encode to base64
    const encoded = btoa(JSON.stringify(license));
    return encoded;
  }

  private generateKey(): string {
    // Generate a unique license key
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 15);
    return `HMPOS-${timestamp}-${random}`.toUpperCase();
  }

  async getLicenseInfo(): Promise<{
    hasLicense: boolean;
    isValid: boolean;
    type: string | null;
    clientName: string | null;
    expiresAt: string | null;
    daysRemaining: number | null;
  }> {
    const license = await this.getLicense();
    
    if (!license) {
      return {
        hasLicense: false,
        isValid: false,
        type: null,
        clientName: null,
        expiresAt: null,
        daysRemaining: null,
      };
    }

    let daysRemaining = null;
    if (license.expiresAt) {
      const expiry = new Date(license.expiresAt);
      const now = new Date();
      daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      hasLicense: true,
      isValid: license.status === 'active',
      type: license.type,
      clientName: license.clientName,
      expiresAt: license.expiresAt,
      daysRemaining,
    };
  }
}

export const licenseService = new LicenseService();
